import React, { useMemo } from "react";
import styled from "styled-components";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DayData, getMachineColor } from "../types";
import { exportCsv } from "../utils/exportCsv";

type Metric = "parts" | "minutes";

interface PieChartSectionProps {
  data: DayData[];
  metric: Metric;
}

interface PieSlice {
  name: string;
  value: number;
  machineType: string;
  machineNumber: number;
  shift: "Day" | "Night";
}

const TITLES: Record<Metric, string> = {
  parts: "Total Parts by Machine & Shift",
  minutes: "Total Minutes by Machine & Shift",
};

const CSV_FILENAMES: Record<Metric, string> = {
  parts: "parts_summary.csv",
  minutes: "minutes_summary.csv",
};

const ChartCard = styled.div`
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChartTitle = styled.h3`
  text-align: center;
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
`;

const ExportButton = styled.button`
  margin-top: 12px;
  background: #37474f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #263238;
  }
`;

const TooltipBox = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: #333;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <TooltipBox>
      <strong>{entry.name}</strong>
      <div>{entry.value}</div>
    </TooltipBox>
  );
};

const RADIAN = Math.PI / 180;

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}: any) => {
  if (percent < 0.03) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {value}
    </text>
  );
};

const MACHINE_TYPE_ORDER: Record<string, number> = {
  CNC: 0,
  Edge: 1,
  Saw: 2,
};

function machineTypeSortKey(type: string): number {
  return MACHINE_TYPE_ORDER[type] ?? 99;
}

const PieChartSection: React.FC<PieChartSectionProps> = ({ data, metric }) => {
  const slices = useMemo<PieSlice[]>(() => {
    const agg: Record<string, PieSlice> = {};

    for (const dayData of data) {
      for (const row of dayData.data) {
        const key = `${row.machineType}|${row.machineNumber}|${row.shift}`;
        if (!agg[key]) {
          agg[key] = {
            name: `${row.machineType} ${row.machineNumber} ${row.shift}`,
            value: 0,
            machineType: row.machineType,
            machineNumber: row.machineNumber,
            shift: row.shift,
          };
        }
        agg[key].value +=
          metric === "parts" ? row.totalParts : Math.floor(row.totalSeconds / 60);
      }
    }

    return Object.values(agg)
      .filter((s) => s.value > 0)
      .sort(
        (a, b) =>
          machineTypeSortKey(a.machineType) - machineTypeSortKey(b.machineType) ||
          a.machineNumber - b.machineNumber ||
          (a.shift === "Day" ? -1 : 1)
      );
  }, [data, metric]);

  const handleExport = () => {
    const valueHeader = metric === "parts" ? "Parts" : "Minutes";
    const headers = ["Machine", "Shift", valueHeader];
    const rows = slices.map((s) => ({
      Machine: `${s.machineType} ${s.machineNumber}`,
      Shift: s.shift,
      [valueHeader]: s.value,
    }));
    exportCsv(headers, rows, CSV_FILENAMES[metric]);
  };

  if (slices.length === 0) return null;

  return (
    <ChartCard>
      <ChartTitle>{TITLES[metric]}</ChartTitle>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={120}
            labelLine={false}
            label={renderLabel}
            strokeWidth={2}
            stroke="#fff"
          >
            {slices.map((slice, i) => (
              <Cell
                key={`cell-${i}`}
                fill={getMachineColor(slice.machineType, slice.machineNumber, slice.shift)}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
            formatter={(value: string, entry: any) => (
              <span style={{ color: entry.color }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <ExportButton onClick={handleExport}>
        Export {metric === "parts" ? "Parts" : "Minutes"} Summary
      </ExportButton>
    </ChartCard>
  );
};

export default PieChartSection;
